import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Modal } from "@/components/ui/modal";
import { useContactListStore } from "@/store/contactListStore";
import { useAuthStore } from "@/store/authStore";
import type { ContactListFull, ContactListType, CreateContactListPayload } from "@/types/contacts";

const LIST_TYPE_COLORS: Record<ContactListType, string> = {
  Static: "bg-muted text-muted-foreground",
  Dynamic: "bg-blue-500/10 text-blue-500",
};

function formatDate(iso: string) {
  const d = new Date(iso);
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: diffDays > 365 ? "numeric" : undefined });
}

interface CreateListModalProps {
  accountId: string;
  onClose: () => void;
}

function CreateListModal({ accountId, onClose }: CreateListModalProps) {
  const { createList, isSaving, error } = useContactListStore();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [listType, setListType] = useState<ContactListType>("Static");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const data: CreateContactListPayload = { name: name.trim(), listType };
    if (description.trim()) data.description = description.trim();
    try {
      await createList(accountId, data);
      onClose();
    } catch {
      // error shown from store
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Name <span className="text-destructive">*</span></label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g. VIP Customers"
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Description</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Type</label>
        <div className="flex gap-3">
          {(["Static", "Dynamic"] as ContactListType[]).map((t) => (
            <label key={t} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="listType"
                value={t}
                checked={listType === t}
                onChange={() => setListType(t)}
                className="border-border text-primary focus:ring-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">{t}</span>
                <p className="text-xs text-muted-foreground">
                  {t === "Static" ? "Manually managed membership" : "Filter-based membership (coming soon)"}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors">Cancel</button>
        <Button type="submit" disabled={isSaving || !name.trim()}>{isSaving ? "Creating…" : "Create list"}</Button>
      </div>
    </form>
  );
}

export default function ListsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accountId = user?.currentAccountId ?? user?.ownedAccountId ?? "";

  const { lists, isLoading, isSaving, error, fetchLists, deleteList } = useContactListStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ContactListFull | null>(null);

  useEffect(() => {
    if (accountId) fetchLists(accountId);
  }, [accountId, fetchLists]);

  const handleDelete = async () => {
    if (!deleteTarget || !accountId) return;
    await deleteList(accountId, deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <ProtectedRoute>
      <div className="lw-lists-page container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="lw-lists-header flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Contact Lists</h1>
              <p className="mt-1 text-sm text-muted-foreground">Organise your audience into lists and segments</p>
            </div>
            <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New List
            </Button>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
                Loading…
              </div>
            </div>
          )}

          {!isLoading && !error && lists.length === 0 && (
            <div className="lw-lists-empty rounded-lg border border-dashed border-border p-16 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="mt-4 text-base font-semibold text-foreground">No lists yet</h3>
              <p className="mt-1.5 text-sm text-muted-foreground max-w-xs mx-auto">
                Create lists to organise your contacts and use them as campaign recipients.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New List
              </button>
            </div>
          )}

          {!isLoading && !error && lists.length > 0 && (
            <div className="lw-lists-table overflow-hidden rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Contacts</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Created</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lists.map((list) => (
                    <tr
                      key={list.id}
                      className="bg-card hover:bg-accent/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/audience/lists/${list.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{list.name}</div>
                        {list.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{list.description}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${LIST_TYPE_COLORS[list.listType]}`}>
                          {list.listType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">{list.contactCount.toLocaleString()}</td>
                      <td className="hidden sm:table-cell px-4 py-3">
                        <span className={`text-xs font-medium ${list.isActive ? "text-green-600" : "text-muted-foreground"}`}>
                          {list.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-muted-foreground text-xs">{formatDate(list.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(list); }}
                          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors mr-2"
                          title="Delete list"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <span className="text-xs text-primary hover:underline">View →</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="New List" size="sm">
        <CreateListModal accountId={accountId} onClose={() => setShowCreateModal(false)} />
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title="Delete List"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will remove all list memberships. Contacts themselves will not be deleted.`}
        confirmLabel={isSaving ? "Deleting…" : "Delete"}
        variant="destructive"
      />
    </ProtectedRoute>
  );
}
